import { useCallback, useEffect, useRef } from "react";

import { type AspectRatio, type DragOffset } from "./helpers";
import { useLatestRef } from "../../common/useLatestRef";

import { generateFitModeCrop, generateSquareCrop, type FitImageResult } from "@/components/ImageUploader/utils";

export type EditorState = {
  aspectRatio: AspectRatio | null;
  dragOffset: DragOffset;
  fittedImage: FitImageResult | null;
  imageDataUrl: string;
  isFitMode: boolean;
  isLoading: boolean;
  targetSize: number;
  useWhiteBackground?: boolean;
};

type UseThrottledPreviewUpdateParams = {
  editorState: EditorState;
  onPreviewUpdate?: (preview: string) => void;
  previewSize?: number;
};

const THROTTLE_DELAY_MS = 100;
const DEFAULT_PREVIEW_OUTPUT_SIZE = 400;

export const useThrottledPreviewUpdate = ({
  editorState,
  onPreviewUpdate,
  previewSize = DEFAULT_PREVIEW_OUTPUT_SIZE,
}: UseThrottledPreviewUpdateParams) => {
  const { fittedImage, imageDataUrl, isLoading } = editorState;

  const previewThrottleRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateTimeRef = useRef(0);

  // Use refs to avoid stale closures in callbacks
  const stateRef = useLatestRef(editorState);
  const onPreviewUpdateRef = useLatestRef(onPreviewUpdate);

  // Reset throttle timestamp when image changes
  useEffect(() => {
    lastUpdateTimeRef.current = 0;
  }, [imageDataUrl]);

  const generatePreview = useCallback(async (): Promise<string | null> => {
    const state = stateRef.current;
    if (!state.fittedImage) {
      return null;
    }

    if (state.aspectRatio === "square") {
      return state.fittedImage.dataUrl;
    }

    if (state.isFitMode && state.aspectRatio) {
      return generateFitModeCrop(
        state.imageDataUrl,
        state.aspectRatio,
        state.targetSize,
        false,
        previewSize,
        state.useWhiteBackground
      );
    }

    return generateSquareCrop(
      state.fittedImage.dataUrl,
      state.fittedImage.width,
      state.fittedImage.height,
      state.dragOffset,
      state.targetSize,
      previewSize
    );
  }, [stateRef, previewSize]);

  const updatePreview = useCallback(async () => {
    if (!onPreviewUpdateRef.current || !stateRef.current.fittedImage || stateRef.current.isLoading) {
      return;
    }

    const previewDataUrl = await generatePreview();
    if (previewDataUrl && onPreviewUpdateRef.current) {
      onPreviewUpdateRef.current(previewDataUrl);
      lastUpdateTimeRef.current = Date.now();
    }
  }, [generatePreview, onPreviewUpdateRef, stateRef]);

  const schedulePreviewUpdate = useCallback(() => {
    if (!onPreviewUpdateRef.current || !fittedImage || isLoading) {
      return;
    }

    if (previewThrottleRef.current) {
      clearTimeout(previewThrottleRef.current);
    }

    const timeSinceLastUpdate = Date.now() - lastUpdateTimeRef.current;

    if (timeSinceLastUpdate >= THROTTLE_DELAY_MS) {
      void updatePreview();
    } else {
      const delay = THROTTLE_DELAY_MS - timeSinceLastUpdate;
      previewThrottleRef.current = setTimeout(() => {
        void updatePreview();
      }, delay);
    }
  }, [fittedImage, isLoading, onPreviewUpdateRef, updatePreview]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (previewThrottleRef.current) {
        clearTimeout(previewThrottleRef.current);
      }
    };
  }, []);

  // Generate initial preview when image is ready
  useEffect(() => {
    if (!fittedImage || isLoading || lastUpdateTimeRef.current !== 0) {
      return;
    }
    void updatePreview();
  }, [fittedImage, isLoading, updatePreview]);

  return { schedulePreviewUpdate };
};
